import { gatewayApi } from './api';

const unwrap = (response) => response.data?.data ?? response.data;

const cleanParams = (params) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== '' && v != null));

// Gateway list and detail always include rejectReason (a string, or null when
// order-service did not supply one). Copy own keys as-is so null is preserved.
const mapOrder = (order) => {
  if (!order || typeof order !== 'object' || Array.isArray(order)) return order;
  const mapped = {};
  Object.keys(order).forEach((key) => {
    mapped[key] = order[key];
  });
  return mapped;
};

const mapOrdersPayload = (payload) => {
  if (!payload || typeof payload !== 'object' || !Array.isArray(payload.orders)) {
    return payload;
  }
  return {
    ...payload,
    orders: payload.orders.map(mapOrder),
  };
};

export const ordersService = {
  async listOrders(params = {}) {
    const response = await gatewayApi.get('/v1/orders', { params: cleanParams(params) });
    return mapOrdersPayload(unwrap(response));
  },

  async getOrder(id) {
    const response = await gatewayApi.get(`/v1/orders/${id}`);
    return mapOrder(unwrap(response));
  },

  // `idempotencyKey` may be supplied by the caller so that retrying the same
  // logical order placement (e.g. re-clicking "Confirm" after a network error)
  // reuses the same key instead of minting a new one, which would defeat
  // server-side de-duplication and risk placing the order twice.
  async placeBuy(payload, idempotencyKey) {
    const key = idempotencyKey || window.crypto?.randomUUID?.() || `buy-${Date.now()}`;
    const response = await gatewayApi.post('/v1/orders/buy', payload, {
      headers: { 'idempotency-key': key },
    });
    return unwrap(response);
  },

  async placeSell(payload, idempotencyKey) {
    const key = idempotencyKey || window.crypto?.randomUUID?.() || `sell-${Date.now()}`;
    const response = await gatewayApi.post('/v1/orders/sell', payload, {
      headers: { 'idempotency-key': key },
    });
    return unwrap(response);
  },

  async previewBuy(payload) {
    const response = await gatewayApi.post('/v1/orders/buy/preview', payload);
    return unwrap(response);
  },

  async previewSell(payload) {
    const response = await gatewayApi.post('/v1/orders/sell/preview', payload);
    return unwrap(response);
  },

  async cancelOrder(id) {
    const response = await gatewayApi.post(`/v1/orders/${id}/cancel`);
    return unwrap(response);
  },
};

export default ordersService;
